import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { AppIcon } from "@/shared/components/AppIcon";
import { platformSession } from "@/core/api/platformApi";
import { getErrorMessage } from "@/core/utils/errors";

export const PlatformLoginPage = (): React.ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loginMode, setLoginMode] = useState<"admin" | "staff">("admin");
  const [accessKey, setAccessKey] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(() =>
    searchParams.get("reason") === "expired"
      ? "نشست مدیریت پلتفرم منقضی شده است؛ دوباره وارد شوید."
      : null,
  );

  const submit = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (loginMode === "admin") {
        await platformSession.login(accessKey);
      } else {
        await platformSession.loginStaff(username, password);
      }
      navigate("/platform/tenants", { replace: true });
    } catch (loginError) {
      setError(getErrorMessage(loginError));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="grid min-h-dvh bg-slate-950 p-3 sm:p-8" dir="rtl">
      <section className="m-auto grid w-full max-w-5xl overflow-hidden rounded-3xl bg-white shadow-2xl shadow-slate-950/30 sm:rounded-[2rem] lg:grid-cols-[1.05fr_0.95fr]">
        <div className="order-2 bg-gradient-to-bl from-brand-600 via-violet-600 to-slate-950 p-6 text-white sm:p-12 lg:order-1">
          <span className="grid size-12 place-items-center rounded-2xl bg-white/15 text-xl font-black">
            C
          </span>
          <p className="mt-8 text-sm font-bold text-brand-100 sm:mt-16">
            مدیریت مرکزی CRM
          </p>
          <h1 className="mt-3 text-2xl font-black leading-tight sm:text-4xl">
            شرکت‌ها و لایسنس‌ها، در یک فضای امن
          </h1>
          <p className="mt-5 max-w-md text-sm leading-8 text-white/75">
            ایجاد فضای CRM برای هر شرکت، تعیین ظرفیت مشتریان و کنترل تاریخ
            اعتبار اشتراک.
          </p>
          <div className="mt-10 hidden space-y-3 text-sm font-bold text-white/85 sm:block">
            <p>• نشست کوتاه‌مدت و مستقل از حساب شرکت‌ها</p>
            <p>• بدون ذخیره‌سازی کلید مدیر در مرورگر</p>
            <p>• دسترسی فقط به مدیریت پلتفرم</p>
          </div>
        </div>
        <form className="order-1 p-6 sm:p-12 lg:order-2" onSubmit={(event) => void submit(event)}>
          <span className="grid size-11 place-items-center rounded-2xl bg-brand-50 text-brand-600">
            <AppIcon name="settings" size={21} />
          </span>
          <h2 className="mt-6 text-2xl font-black text-ink-950">
            {loginMode === "admin" ? "ورود مدیر زیرساخت" : "ورود مدیر پلتفرم"}
          </h2>
          <p className="mt-2 text-sm leading-7 text-slate-500">
            {loginMode === "admin"
              ? "کلید خصوصی مدیر را وارد کنید. این کلید فقط برای ساخت نشست کوتاه‌مدت ارسال می‌شود."
              : "با نام کاربری و رمز عبور اختصاصی خود وارد شوید تا فعالیت‌ها به نام شما ثبت شود."}
          </p>
          <div className="mt-6 grid grid-cols-2 rounded-xl bg-slate-100 p-1 text-xs font-black">
            <button
              className={`rounded-lg px-3 py-2.5 transition ${loginMode === "admin" ? "bg-white text-brand-700 shadow-sm" : "text-slate-500"}`}
              onClick={() => { setLoginMode("admin"); setError(null); }}
              type="button"
            >
              مدیر زیرساخت
            </button>
            <button
              className={`rounded-lg px-3 py-2.5 transition ${loginMode === "staff" ? "bg-white text-brand-700 shadow-sm" : "text-slate-500"}`}
              onClick={() => { setLoginMode("staff"); setError(null); }}
              type="button"
            >
              مدیر پلتفرم
            </button>
          </div>
          {error ? (
            <p className="mt-5 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">
              {error}
            </p>
          ) : null}
          {loginMode === "admin" ? (
            <label className="mt-7 block">
              <span className="mb-2 block text-sm font-bold text-slate-700">
                کلید دسترسی
              </span>
              <input
                autoComplete="current-password"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-left font-sans text-sm outline-none transition focus:border-brand-500 focus:ring-4 focus:ring-brand-50"
                dir="ltr"
                onChange={(event) => setAccessKey(event.target.value)}
                placeholder="Platform administrator key"
                required
                type="password"
                value={accessKey}
              />
            </label>
          ) : (
            <div className="mt-7 space-y-4">
              <label className="block">
                <span className="mb-2 block text-sm font-bold text-slate-700">نام کاربری</span>
                <input
                  autoComplete="username"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-left font-sans text-sm outline-none transition focus:border-brand-500 focus:ring-4 focus:ring-brand-50"
                  dir="ltr"
                  onChange={(event) => setUsername(event.target.value)}
                  placeholder="sales.agent"
                  required
                  value={username}
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-bold text-slate-700">رمز عبور</span>
                <input
                  autoComplete="current-password"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-left font-sans text-sm outline-none transition focus:border-brand-500 focus:ring-4 focus:ring-brand-50"
                  dir="ltr"
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="••••••••"
                  required
                  type="password"
                  value={password}
                />
              </label>
            </div>
          )}
          <button
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 py-3.5 text-sm font-black text-white shadow-lg shadow-brand-500/25 transition hover:bg-brand-700 disabled:cursor-wait disabled:opacity-70"
            disabled={submitting}
            type="submit"
          >
            {submitting ? "در حال بررسی…" : "ورود به مدیریت پلتفرم"}{" "}
            <AppIcon name="arrow-left" size={17} />
          </button>
          <a
            className="mt-6 block text-center text-xs font-bold text-slate-400 hover:text-brand-600"
            href="/login"
          >
            بازگشت به ورود سازمان‌ها
          </a>
        </form>
      </section>
    </main>
  );
};
